import { ENV } from '../../../../config/runtimeEnv';
import React, { useState } from "react";
import { getSessionUser } from "../../../../utils/auth/sessionUser";
import { Alert, Button, Dropdown, Segmented, Tooltip, Typography } from "antd";
import { FileTextOutlined, ExperimentOutlined as DnaOutlined, EyeOutlined, ExportOutlined, LockOutlined, DownOutlined, UpOutlined, LoadingOutlined, ReloadOutlined } from "@ant-design/icons";
import { format } from "date-fns";
import { hasSubscription } from "../../../../utils/subscriptions/subscriptionService";
import { isGeneticAnalysisResult } from "../../../../utils/healthcare/geneticTest";

import EmptyState from "../../../../components/shared/EmptyState";
import StatusTag from "../../../../components/shared/StatusTag";
import { useNotifications } from "../../../../context/NotificationContext";
import { retryTestOrderAnalysis } from "../../../../api/healthcare/Testing/TestApi";
import { downloadGenotypeExport, genotypeExportErrorMessage } from "../../../../api/labiq/GenotypeExportApi";
import type { GenotypeExportFormat } from "../../../../api/labiq/GenotypeExportApi";

import { Test, TestResult } from "../../../../types/healthcare/TestTypes";
import { Patient } from "../../../../types/healthcare/PatientTypes";

import BloodAnalysisModal from "../../../gene.codes/bloods/BloodAnalysisModal";
import ResultRangeBar from "../../../gene.codes/bloods/BloodAnalysiscomponents/ResultRangeBar";
import ResultReviewPanel from "./ResultReviewPanel";
import BloodResultsTable, { BloodResultsViewMode } from "./BloodResultsTable";
import GeneticSampleAnalysisModal from "../../../gene.codes/GeneticWheel/WheelModal";
import GeneCodesTab from "./GeneCodesTab";
import ReportTab from "./ReportTab";
import { activatePatientAuthSamples, createAuthSamples, fetchPatientAuthSamples } from "../../../../api/geneCodes/AuthSamples";
import { createOrUpdateSamplesPanels, fetchPanels } from "../../../../api/geneCodes/PanelApi";
import { openGeneCodes } from "../../../../utils/geneCodes/geneCodesSite";
import { Sample } from "../../../../types/lab/SampleTypes";
import { Tabs } from "antd";
import { useBloodRangeContext } from "../../../../hooks/useBloodRangeContext";
import { withLabRangeKeys } from "../../../../utils/geneCodes/bloodTestShape";

const { Text } = Typography;

// Legacy/imported orders can carry a raw internal slug (e.g. "other_lab") as the
// result's test_type. Render slugs as a readable sentence-case label; leave values
// that already look like human labels untouched.
const formatAnalysisType = (value?: string | null): string => {
	if (!value) return "—";
	if (/^[a-z0-9]+(?:[_-][a-z0-9]+)*$/.test(value)) {
		const words = value.replace(/[_-]+/g, " ").trim();
		return words.charAt(0).toUpperCase() + words.slice(1);
	}
	return value;
};

interface TestResultsTabProps {
	currentOrder: Partial<Test>;
	patient?: Patient;
	allTests?: Test[];
	onResultSaved?: () => void;
}

const TestResultsTab: React.FC<TestResultsTabProps> = ({ currentOrder, patient, allTests, onResultSaved }) => {
	const hasGeneticsSubscription = hasSubscription("genetics_analysis");
	const [isBloodAnalysisModalOpen, setIsBloodAnalysisModalOpen] = useState(false);
	const [isGeneticModalOpen, setIsGeneticModalOpen] = useState(false);
	const [isGeneCodesLoading, setIsGeneCodesLoading] = useState(false);
	const [exportingFormat, setExportingFormat] = useState<GenotypeExportFormat | null>(null);
	const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
	const [bloodResultsViewMode, setBloodResultsViewMode] = useState<BloodResultsViewMode>("report");
	const { rangeContext } = useBloodRangeContext(patient ?? currentOrder.patient);
	const [isRetryingAnalysis, setIsRetryingAnalysis] = useState(false);
	const [retryRequested, setRetryRequested] = useState(false);

	const { showError, showSuccess } = useNotifications();

	// An order can be mid-analysis and already carry some parsed analytes (an XML import
	// writes real rows before the AI step runs), so the standalone "in progress" state is
	// only shown when nothing has been parsed yet — status alone would hide real results.
	const hasParsedResults = (currentOrder.test_results ?? []).some(
		(result) => Array.isArray(result.results) && result.results.length > 0,
	);
	const isAnalysisInProgress = currentOrder.status === "in_progress" && !hasParsedResults;
	const isAnalysisFailed = currentOrder.status === "analysis_failed";
	// A failed analysis keeps whatever was parsed before it failed on screen; the
	// "no results yet" empty state is only right when there is genuinely nothing.
	const showResultRows = !isAnalysisInProgress && !(isAnalysisFailed && !hasParsedResults);

	const handleRetryAnalysis = async () => {
		if (!currentOrder.id) return;
		setIsRetryingAnalysis(true);
		try {
			await retryTestOrderAnalysis(currentOrder.id);
			setRetryRequested(true);
			showSuccess("Analysis restarted. Results will appear here once it completes.");
			// Refresh the order so it re-renders as In Progress.
			onResultSaved?.();
		} catch (error: any) {
			showError(error?.response?.data?.message || "Failed to restart the analysis. Please try again.");
		} finally {
			setIsRetryingAnalysis(false);
		}
	};

	const toggleExpanded = (id: number) => {
		setExpandedIds((prev) => {
			const next = new Set(prev);
			next.has(id) ? next.delete(id) : next.add(id);
			return next;
		});
	};

	const handleGeneticAnalysisClick = (sampleId: string) => {
		if (ENV.VITE_GENETIC_WHEEL !== "true") {
			handleOpenGeneCodes(sampleId);
		} else {
			setIsGeneticModalOpen(true);
		}
	};

	const handleOpenGeneCodes = async (sampleId: string) => {
		setIsGeneCodesLoading(true);
		try {
			const user = getSessionUser();
			const authSample = await createAuthSamples(user.email, sampleId);

			if (authSample.success) {
				const { data } = await fetchPanels();
				const panelIds = data.map((panel) => panel.id_panels);
				const smpls = await fetchPatientAuthSamples(user.email);
				const matchingSample = smpls.find((sample: Sample) => sample.sampleID === sampleId);
				await createOrUpdateSamplesPanels(matchingSample.idauth_samples, panelIds);
				await activatePatientAuthSamples(matchingSample.idauth_samples);
			}

			openGeneCodes();
		} catch (error) {
			console.error("Error during gene codes login:", error);
		} finally {
			setIsGeneCodesLoading(false);
		}
	};

	const handleGenotypeExport = async (sampleId: string, exportFormat: GenotypeExportFormat) => {
		if (exportingFormat) return;
		setExportingFormat(exportFormat);
		try {
			await downloadGenotypeExport(sampleId, exportFormat);
		} catch (error) {
			// responseType 'blob' hides the error body, so only the status can be mapped to a message
			showError(genotypeExportErrorMessage(error));
		} finally {
			setExportingFormat(null);
		}
	};

	const showBloodAnalysisButton =
		currentOrder?.test_category === "blood_tests" &&
		(currentOrder?.blood_test_id != null || currentOrder?.bloodTest != null);

	const renderResultRows = () => {
		const testResults = currentOrder?.test_results || [];

		if (!testResults.length) {
			return (
				<EmptyState
					icon={<FileTextOutlined style={{ fontSize: 48, color: "var(--mg-text-disabled)" }} />}
					title="No results available"
					description="Results will appear here once the test is completed and analysed."
				/>
			);
		}

		return (
			<div style={{ display: "flex", flexDirection: "column" }}>
				{/* Single bloods analysis entry point for the whole order */}
				{showBloodAnalysisButton && (
					<div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
						<Button type="primary" size="small" icon={<EyeOutlined />} onClick={() => setIsBloodAnalysisModalOpen(true)}>
							Open full analysis
						</Button>
					</div>
				)}
				{currentOrder?.test_category === "blood_tests" ? (
					<>
						<div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
							<Segmented
								value={bloodResultsViewMode}
								onChange={(value) => setBloodResultsViewMode(value as BloodResultsViewMode)}
								options={[
									{ label: "Report order", value: "report" },
									{ label: "Grouped", value: "grouped" },
								]}
								size="small"
							/>
						</div>
						<BloodResultsTable currentOrder={currentOrder} mode={bloodResultsViewMode} patient={patient ?? currentOrder.patient} />
					</>
				) : (
				<>
				{/* Header row */}
				<div style={{
					display: "flex",
					alignItems: "center",
					gap: 12,
					padding: "6px 0",
					borderBottom: "1px solid var(--mg-border)",
					marginBottom: 2,
				}}>
					<Text style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", color: "var(--mg-text-secondary)", width: 140, flexShrink: 0 }}>Analysis type</Text>
					<Text style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", color: "var(--mg-text-secondary)", width: 150, flexShrink: 0 }}>Status</Text>
					<Text style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", color: "var(--mg-text-secondary)", width: 110, flexShrink: 0 }}>Available</Text>
					<Text style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", color: "var(--mg-text-secondary)", flex: 1 }}>Results in range</Text>
					<div style={{ width: 260, flexShrink: 0 }} />
				</div>

				{testResults.map((result, i) => {
					const isExpanded = expandedIds.has(result.id);
					return (
						<div
							key={result.id}
							style={{ borderBottom: i < testResults.length - 1 ? "1px solid var(--mg-border)" : "none" }}
						>
							{/* Row — click anywhere to expand the inline review section */}
							<div
								onClick={() => toggleExpanded(result.id)}
								style={{
									display: "flex",
									alignItems: "flex-start",
									gap: 12,
									padding: "10px 0",
									cursor: "pointer",
								}}
							>
								{/* Analysis type */}
								<div style={{ width: 140, flexShrink: 0 }}>
									<Text style={{ fontSize: 12, fontWeight: 500 }}>{formatAnalysisType(result.test_type)}</Text>
								</div>

								{/* Status */}
								<div style={{ width: 150, flexShrink: 0 }}>
									<StatusTag status={result.status} color="black" />
								</div>

								{/* Available date */}
								<div style={{ width: 110, flexShrink: 0 }}>
									{result.results_available_date ? (
										<Text style={{ fontSize: 12 }}>{format(new Date(result.results_available_date), "dd/MM/yyyy")}</Text>
									) : (
										<Text type="secondary" style={{ fontSize: 12 }}>—</Text>
									)}
								</div>

								{/* Results in range — a compact bar per analyte for a quick at-a-glance read */}
								<div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 6 }}>
									{Array.isArray(result.results) && result.results.length > 0 ? (
										result.results.map((analyte: any, idx: number) => (
											<div key={analyte.test_code || analyte.test_name || idx} style={{ display: "flex", alignItems: "center", gap: 8 }}>
												<Text style={{ fontSize: 11, width: 120, flexShrink: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={analyte.test_name}>
													{analyte.test_name}
												</Text>
												<Text style={{ fontSize: 11, width: 70, flexShrink: 0, textAlign: "right", color: "var(--mg-text-secondary)" }}>
													{analyte.result_value}{analyte.units ? ` ${analyte.units}` : ""}
												</Text>
												<div style={{ flex: 1, minWidth: 120 }}>
													<ResultRangeBar result={withLabRangeKeys(analyte)} rangeContext={rangeContext} compact showLabels={false} height={8} />
												</div>
											</div>
										))
									) : (
										<Text type="secondary" style={{ fontSize: 12 }}>—</Text>
									)}
								</div>

								{/* Actions */}
								<div style={{ width: 260, flexShrink: 0, display: "flex", justifyContent: "flex-end", gap: 8 }}>
									{isGeneticAnalysisResult(result) && result.status !== "pending" && (
										<Tooltip title={!hasGeneticsSubscription ? "Requires Genetics Analysis subscription" : undefined}>
											<Button
												type="primary"
												size="small"
												loading={isGeneCodesLoading}
												disabled={isGeneCodesLoading || !hasGeneticsSubscription}
												icon={!hasGeneticsSubscription ? <LockOutlined /> : undefined}
												onClick={(e) => {
													e.stopPropagation();
													handleGeneticAnalysisClick(currentOrder.sample_id!);
												}}
											>
												{isGeneCodesLoading ? "Opening…" : "Open analysis"}
											</Button>
										</Tooltip>
									)}
									{isGeneticAnalysisResult(result) && result.status !== "pending" && currentOrder.sample_id && (
										// The span is the tooltip's hover target: antd cannot attach one to a Dropdown child.
										<Tooltip title={!hasGeneticsSubscription ? "Requires Genetics Analysis subscription" : undefined}>
											<span>
												<Dropdown
													trigger={["click"]}
													disabled={!hasGeneticsSubscription || exportingFormat !== null}
													menu={{
														items: [
															{ key: "csv", label: "Export CSV" },
															{ key: "pdf", label: "Export PDF" },
														],
														onClick: ({ key, domEvent }) => {
															domEvent.stopPropagation();
															handleGenotypeExport(currentOrder.sample_id!, key as GenotypeExportFormat);
														},
													}}
												>
													<Button
														size="small"
														loading={exportingFormat !== null}
														disabled={!hasGeneticsSubscription || exportingFormat !== null}
														icon={<ExportOutlined />}
														onClick={(e) => e.stopPropagation()}
													>
														Export
													</Button>
												</Dropdown>
											</span>
										</Tooltip>
									)}
									<Tooltip title={isExpanded ? "Hide review" : "Review result"}>
										<Button
											type="text"
											size="small"
											icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
											onClick={(e) => {
												e.stopPropagation();
												toggleExpanded(result.id);
											}}
										/>
									</Tooltip>
								</div>
							</div>

							{/* Inline review section — replaces the old result viewer modal */}
							{isExpanded && (
								<div style={{ padding: "4px 0 14px", maxWidth: 720 }}>
									<ResultReviewPanel
										result={result}
										testId={currentOrder.id}
										onSaved={() => {
											toggleExpanded(result.id);
											onResultSaved?.();
										}}
									/>
								</div>
							)}
						</div>
					);
				})}
				</>
				)}
			</div>
		);
	};

	const resultsContent = (
		<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
			{isAnalysisFailed && (
				<Alert
					type="error"
					showIcon
					message="Analysis failed"
					description={
						hasParsedResults
							? "The automated analysis of this order did not complete. The results parsed before it failed are shown below."
							: "The automated analysis of this order did not complete and no results were parsed."
					}
					action={
						<Button
							size="small"
							danger
							icon={<ReloadOutlined />}
							loading={isRetryingAnalysis}
							disabled={isRetryingAnalysis || retryRequested || !currentOrder.id}
							onClick={handleRetryAnalysis}
						>
							Retry analysis
						</Button>
					}
				/>
			)}
			{isAnalysisInProgress && (
				<EmptyState
					icon={<LoadingOutlined style={{ fontSize: 40, color: "var(--mg-primary)" }} />}
					title="Analysis in progress"
					description="Results will appear here when it completes."
				/>
			)}
			{showResultRows && renderResultRows()}
			{currentOrder.test_category === "genetic_testing" && patient && (
				<ReportTab clientSampleId={currentOrder.sample_id} patient={patient} />
			)}
		</div>
	);

	// Genetic testing: show results + optional patient access tab
	if (currentOrder?.test_category === "genetic_testing" && patient) {
		const tabItems = [
			{
				key: "results",
				label: "Test Results",
				icon: <FileTextOutlined />,
				children: resultsContent,
			},
			...(hasGeneticsSubscription
				? [
						{
							key: "access",
							label: "Patient Access",
							icon: <DnaOutlined />,
							children: <GeneCodesTab clientSampleId={currentOrder.sample_id} patient={patient} />,
						},
					]
				: []),
		];

		return (
			<div>
				<Tabs items={tabItems} size="small" />
				<GeneticSampleAnalysisModal
					isOpen={isGeneticModalOpen}
					onClose={() => setIsGeneticModalOpen(false)}
					patient={currentOrder.patient}
					sample={{ sampleID: currentOrder.sample_id! } as any}
					surface="practitioner-results"
				/>
			</div>
		);
	}

	return (
		<div>
			{resultsContent}
			<BloodAnalysisModal isOpen={isBloodAnalysisModalOpen} onClose={() => setIsBloodAnalysisModalOpen(false)} test={currentOrder as Test} patient={patient} alltests={allTests} />
		</div>
	);
};

export default TestResultsTab;
